import {BooleanHookState, useBoolean} from '@containers/hooks'
import React from 'react'
import {Lang} from 'yougile-lang'
import {
  getGenderOfDayType,
  getTimeOfDayWithScheduleTime,
  Schedule,
  ScheduleType,
  SelectedDayNumber,
  selectedDayNumberArr,
  SelectedDayType,
  selectedDayTypeArr,
  toLocalSchedule,
  toServerSchedule
} from 'yougile-model'
import {Time, Util} from 'yougile-util'

export const useScheduleChooser = (
  schedule: Schedule,
  updateServerTime: (sch: Schedule) => void,
  latestRepeatTime: number,
  valid: BooleanHookState
) => {
  const [eachValue, setEachValue] = React.useState<number | undefined>(
    schedule.each ?? 1
  )
  const eachValueValid = useBoolean(true)
  const timeValid = useBoolean(true)
  const dateValid = useBoolean(true)

  React.useEffect(() => {
    valid.setValue(timeValid.value && dateValid.value && eachValueValid.value)
  }, [timeValid.value, dateValid.value, eachValueValid.value])

  const update = (newLocalSchedule: Schedule) =>
    updateServerTime(toServerSchedule(newLocalSchedule, true))

  const updateType = (type: ScheduleType) => {
    update({
      ...schedule,
      type,
      daysOfWeek: schedule.daysOfWeek ?? [5],
      daysOfMonth: schedule.daysOfMonth ?? [1],
      monthsOfYear: schedule.monthsOfYear ?? [1]
    })
  }

  const updateTime = (hour: number, minute: number) =>
    update({...schedule, hour, minute})

  const updateEach = (each: number | undefined) => {
    each = Math.floor(each ?? 0)
    if (each > 999) {
      return
    }
    setEachValue(each || undefined)
    const isValid = !!each && each > 0
    if (isValid) {
      update({...schedule, each})
    }
    eachValueValid.setValue(isValid)
  }

  const updateMonthsOfYear = (monthsOfYear: number[]) =>
    update({...schedule, monthsOfYear})

  const updateDaysOfMonth = (daysOfMonth: number[]) =>
    update({...schedule, daysOfMonth})

  const updateDaysOfWeek = (daysOfWeek: number[]) =>
    update({...schedule, daysOfWeek})

  const onDayOfMonthClick = () =>
    update({...schedule, selectedDayOfMonth: undefined})

  const onSelectedDayOfMonthClick = () =>
    update({...schedule, selectedDayOfMonth: {dayNumber: 1, dayType: 0}})

  const selectedDayNumberOptions = getSelectedDayNumberOptions(
    Lang.msg,
    schedule.selectedDayOfMonth?.dayType
  )
  const selectedDayTypeOptions = getSelectedDayTypeOptions(Lang.msg)

  const startDate: Date = new Date(
    schedule.since ?? latestRepeatTime ?? Time.now()
  )
  const updateStartDate = (date: Date) =>
    update({
      ...schedule,
      since: getTimeOfDayWithScheduleTime(
        toLocalSchedule(schedule),
        date?.getTime()
      )
    })

  const updateSelectedDayNumber = (dayNumber: string) => {
    const num = +dayNumber
    update({
      ...schedule,
      selectedDayOfMonth: {
        dayNumber: (!isNaN(num) ? num : dayNumber) as SelectedDayNumber,
        dayType: schedule.selectedDayOfMonth.dayType
      }
    })
  }

  const updateSelectedDayType = (dayType: string) => {
    const t = +dayType
    update({
      ...schedule,
      selectedDayOfMonth: {
        dayNumber: schedule.selectedDayOfMonth.dayNumber,
        dayType: (!isNaN(t) ? t : dayType) as SelectedDayType
      }
    })
  }

  return {
    eachValue,
    timeValid,
    dateValid,
    updateType,
    updateTime,
    updateEach,
    updateMonthsOfYear,
    updateDaysOfMonth,
    updateDaysOfWeek,
    updateStartDate,
    updateSelectedDayNumber,
    updateSelectedDayType,
    onDayOfMonthClick,
    onSelectedDayOfMonthClick,
    selectedDayNumberOptions,
    selectedDayTypeOptions,
    startDate
  }
}

const DAY_NUMBERS = selectedDayNumberArr.map(v => String(v))
const getSelectedDayNumberOptions = (
  msg: typeof Lang.msg,
  dayType?: SelectedDayType
) => {
  return DAY_NUMBERS.map(num => ({
    label: Util.capitalize(
      msg.report.selectedDayNumber(num, getGenderOfDayType(dayType))
    ),
    value: num
  }))
}

const DAY_TYPES = selectedDayTypeArr.map(v => String(v))
const getSelectedDayTypeOptions = (msg: typeof Lang.msg) => {
  return DAY_TYPES.map(t => ({
    label: Util.capitalize(msg.report.selectedDayType(t)),
    value: t
  }))
}
